import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { temporaryLocationsAPI } from '../../../services/api';
import { FriendLocation, TemporaryLocationFormData, CreateTemporaryLocationData, FriendGroupWithMembers } from '../../../types';
import { TempLocationModal } from './modals';
import { CustomDateInput } from './CustomDateInput';
import { getLocationTypes } from '../../../components/Map/locationTypes';

interface TempLocationFormManagerProps {
  // Core dependencies
  t: (key: string) => string;
  locations: FriendLocation[];
  friendGroups: Map<number, { id: number; name: string; color?: string }[]>;
  groups: FriendGroupWithMembers[];

  // State from parent (Map.tsx)
  showTempLocationModal: boolean;
  setShowTempLocationModal: (show: boolean) => void;
  tempLocationData: TemporaryLocationFormData;
  setTempLocationData: React.Dispatch<React.SetStateAction<TemporaryLocationFormData>>;

  // Geocoding state from parent
  addressSearchQuery: string;
  setAddressSearchQuery: (query: string) => void;
  isSearchingAddress: boolean;
  addressSuggestions: any[];
  selectAddress: (suggestion: any) => void;

  // Callbacks to refresh data after create/update/delete
  onRefreshTempLocations: () => void;
  onRefreshMyTempLocations: () => void;

  // Group creation callback
  onCreateGroup: (name: string, color: string) => Promise<number>;

  // Optional: Called when modal is closed without saving (to restore place panel)
  onCancel?: () => void;

  // Optional: Called after successfully saving
  onSaveSuccess?: () => void;
}

/**
 * Manager component that encapsulates all temporary location form logic
 * Reduces Map.tsx complexity by handling form state, validation, and API calls
 */
export const TempLocationFormManager: React.FC<TempLocationFormManagerProps> = ({
  t,
  locations,
  friendGroups,
  groups,
  showTempLocationModal,
  setShowTempLocationModal,
  tempLocationData,
  setTempLocationData,
  addressSearchQuery,
  setAddressSearchQuery,
  isSearchingAddress,
  addressSuggestions,
  selectAddress,
  onRefreshTempLocations,
  onRefreshMyTempLocations,
  onCreateGroup,
  onCancel,
  onSaveSuccess,
}) => {
  const navigate = useNavigate();
  const LOCATION_TYPES = getLocationTypes(t);

  // Local state for form UI
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [showBlacklist, setShowBlacklist] = useState(false);
  const [blacklistSearchQuery, setBlacklistSearchQuery] = useState('');
  const [blacklistedFriendIds, setBlacklistedFriendIds] = useState<number[]>([]);
  const [dateMode, setDateMode] = useState<'day' | 'datetime'>('datetime');
  const [showCreateGroupInTempModal, setShowCreateGroupInTempModal] = useState(false);
  const [newGroupDataInTempModal, setNewGroupDataInTempModal] = useState({ name: '', color: '#4ECDC4' });
  const [loading, setLoading] = useState(false);

  // Date template functions
  const getDefaultDates = useCallback(() => {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 3600000);
    return {
      start: now.toISOString().slice(0, 16),
      end: oneHourLater.toISOString().slice(0, 16),
    };
  }, []);

  const applyNowTemplate = useCallback(() => {
    if (dateMode === 'day') {
      // Mode "Toute la journée" : Aujourd'hui (toute la journée)
      const today = new Date();
      setTempLocationData({
        ...tempLocationData,
        start_datetime: today.toISOString().slice(0, 10),
        end_datetime: today.toISOString().slice(0, 10),
      });
    } else {
      // Mode datetime : Maintenant (1 heure)
      const now = new Date();
      const oneHourLater = new Date(now.getTime() + 3600000);
      setTempLocationData({
        ...tempLocationData,
        start_datetime: now.toISOString().slice(0, 16),
        end_datetime: oneHourLater.toISOString().slice(0, 16),
      });
    }
  }, [tempLocationData, setTempLocationData, dateMode]);

  const applyTonightTemplate = useCallback(() => {
    if (dateMode === 'day') {
      // Mode "Toute la journée" : Cette semaine (lundi à dimanche)
      const today = new Date();
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      setTempLocationData({
        ...tempLocationData,
        start_datetime: monday.toISOString().slice(0, 10),
        end_datetime: sunday.toISOString().slice(0, 10),
      });
    } else {
      // Mode datetime : Ce soir (20h - 23h59)
      const today = new Date();
      today.setHours(20, 0, 0, 0);
      const midnight = new Date(today);
      midnight.setHours(23, 59, 0, 0);
      setTempLocationData({
        ...tempLocationData,
        start_datetime: today.toISOString().slice(0, 16),
        end_datetime: midnight.toISOString().slice(0, 16),
      });
    }
  }, [tempLocationData, setTempLocationData, dateMode]);

  const applyWeekendTemplate = useCallback(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSaturday = dayOfWeek === 6 ? 0 : dayOfWeek === 0 ? 6 : 6 - dayOfWeek;
    const saturday = new Date(today);
    saturday.setDate(today.getDate() + daysUntilSaturday);
    saturday.setHours(0, 0, 0, 0);
    const sunday = new Date(saturday);
    sunday.setDate(saturday.getDate() + 1);
    sunday.setHours(23, 59, 0, 0);
    setTempLocationData({
      ...tempLocationData,
      start_datetime: saturday.toISOString().slice(0, 10),
      end_datetime: sunday.toISOString().slice(0, 10),
    });
    setDateMode('day');
  }, [tempLocationData, setTempLocationData]);

  const formatLocalDateTime = (date: Date): string => {
    return date.toISOString().slice(0, 16);
  };

  // Initialize dates when modal opens
  useEffect(() => {
    if (showTempLocationModal && !tempLocationData.start_datetime && !tempLocationData.end_datetime) {
      const defaults = getDefaultDates();
      setTempLocationData({
        ...tempLocationData,
        start_datetime: defaults.start,
        end_datetime: defaults.end,
      });
    }
  }, [showTempLocationModal, getDefaultDates, tempLocationData, setTempLocationData]);

  // Date format conversion
  useEffect(() => {
    if (showTempLocationModal && tempLocationData.start_datetime && tempLocationData.end_datetime) {
      let convertedStart = tempLocationData.start_datetime;
      let convertedEnd = tempLocationData.end_datetime;

      if (dateMode === 'day') {
        convertedStart = tempLocationData.start_datetime.slice(0, 10);
        convertedEnd = tempLocationData.end_datetime.slice(0, 10);
      } else {
        if (tempLocationData.start_datetime.length === 10) {
          convertedStart = tempLocationData.start_datetime + 'T00:00';
        }
        if (tempLocationData.end_datetime.length === 10) {
          convertedEnd = tempLocationData.end_datetime + 'T23:59';
        }
      }

      if (convertedStart !== tempLocationData.start_datetime || convertedEnd !== tempLocationData.end_datetime) {
        setTempLocationData({
          ...tempLocationData,
          start_datetime: convertedStart,
          end_datetime: convertedEnd,
        });
      }
    }
  }, [dateMode, showTempLocationModal, tempLocationData, setTempLocationData]);

  // Handle create/update
  const handleSave = async () => {
    try {
      setLoading(true);

      if (!tempLocationData.name || !tempLocationData.start_datetime || !tempLocationData.end_datetime) {
        alert(t('map.tempLocation.fillAllFields'));
        return;
      }

      let dataToSubmit: any = { ...tempLocationData };

      // Add address if provided
      if (addressSearchQuery && addressSearchQuery.trim()) {
        dataToSubmit.address = addressSearchQuery.trim();
      }

      // Handle blacklist
      if (blacklistedFriendIds.length > 0) {
        const allFriendIds = locations.map(friend => friend.id);
        const visibleFriendIds = allFriendIds.filter(id => !blacklistedFriendIds.includes(id));

        if (tempLocationData.visibility_type === 'all') {
          dataToSubmit.visibility_type = 'friends';
          dataToSubmit.visible_friend_ids = visibleFriendIds;
        } else if (tempLocationData.visibility_type === 'groups') {
          dataToSubmit.visibility_type = 'friends';
          dataToSubmit.visible_friend_ids = visibleFriendIds;
        }
      }

      // Update or create
      if (dataToSubmit.id) {
        const id = dataToSubmit.id;
        const { id: _, ...updateData } = dataToSubmit;
        await temporaryLocationsAPI.update(id, updateData);
        alert(t('map.tempLocation.updateSuccess'));
      } else {
        await temporaryLocationsAPI.create(dataToSubmit as CreateTemporaryLocationData);
        alert(t('map.tempLocation.createSuccess'));
      }

      // Refresh and close
      setShowTempLocationModal(false);
      setBlacklistedFriendIds([]);
      onRefreshTempLocations();
      onRefreshMyTempLocations();

      // Notify parent that save was successful
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      // Return to profile if needed
      const returnToProfile = localStorage.getItem('returnToProfile');
      if (returnToProfile === 'true') {
        localStorage.removeItem('returnToProfile');
        navigate('/profile');
      }
    } catch (err: any) {
      console.error('Failed to save temporary location:', err);
      alert(t('map.tempLocation.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!tempLocationData.id) return;

    const confirmDelete = window.confirm(t('map.tempLocation.confirmDelete') || 'Êtes-vous sûr de vouloir supprimer ce lieu temporaire ?');
    if (!confirmDelete) return;

    try {
      setLoading(true);
      await temporaryLocationsAPI.delete(tempLocationData.id);
      alert(t('map.tempLocation.deleteSuccess') || 'Lieu temporaire supprimé avec succès');

      setShowTempLocationModal(false);
      onRefreshTempLocations();
      onRefreshMyTempLocations();

      // Notify parent that save/delete was successful
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      // Return to profile if needed
      const returnToProfile = localStorage.getItem('returnToProfile');
      if (returnToProfile === 'true') {
        localStorage.removeItem('returnToProfile');
        navigate('/profile');
      }
    } catch (err: any) {
      console.error('Failed to delete temporary location:', err);
      alert(t('map.tempLocation.deleteFailed') || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setShowTempLocationModal(false);
    setBlacklistedFriendIds([]);

    // Notify parent that modal was closed without saving (to restore place panel)
    if (onCancel) {
      onCancel();
    }

    // Return to profile if needed
    const returnToProfile = localStorage.getItem('returnToProfile');
    if (returnToProfile === 'true') {
      localStorage.removeItem('returnToProfile');
      navigate('/profile');
    }
  };

  // Handle group creation within modal
  const handleCreateGroupInModal = async () => {
    if (!newGroupDataInTempModal.name.trim()) {
      alert('Veuillez entrer un nom de groupe');
      return;
    }

    try {
      const newGroupId = await onCreateGroup(newGroupDataInTempModal.name, newGroupDataInTempModal.color);

      // Add new group to visibility
      setTempLocationData({
        ...tempLocationData,
        visible_group_ids: [...(tempLocationData.visible_group_ids || []), newGroupId]
      });

      // Reset and close group creation form
      setNewGroupDataInTempModal({ name: '', color: '#4ECDC4' });
      setShowCreateGroupInTempModal(false);
    } catch (error) {
      console.error('Failed to create group:', error);
      alert('Erreur lors de la création du groupe');
    }
  };

  return (
    <TempLocationModal
      isOpen={showTempLocationModal}
      onClose={handleClose}
      tempLocationData={tempLocationData}
      setTempLocationData={setTempLocationData}
      addressSearchQuery={addressSearchQuery}
      setAddressSearchQuery={setAddressSearchQuery}
      isSearchingAddress={isSearchingAddress}
      addressSuggestions={addressSuggestions}
      selectAddress={selectAddress}
      showCoordinates={showCoordinates}
      setShowCoordinates={setShowCoordinates}
      LOCATION_TYPES={LOCATION_TYPES}
      groups={groups}
      showBlacklist={showBlacklist}
      setShowBlacklist={setShowBlacklist}
      blacklistSearchQuery={blacklistSearchQuery}
      setBlacklistSearchQuery={setBlacklistSearchQuery}
      locations={locations}
      friendGroups={friendGroups}
      blacklistedFriendIds={blacklistedFriendIds}
      setBlacklistedFriendIds={setBlacklistedFriendIds}
      dateMode={dateMode}
      setDateMode={setDateMode}
      applyNowTemplate={applyNowTemplate}
      applyTonightTemplate={applyTonightTemplate}
      applyWeekendTemplate={applyWeekendTemplate}
      formatLocalDateTime={formatLocalDateTime}
      CustomDateInput={CustomDateInput}
      onSave={handleSave}
      onDelete={handleDelete}
      showCreateGroupInTempModal={showCreateGroupInTempModal}
      setShowCreateGroupInTempModal={setShowCreateGroupInTempModal}
      newGroupDataInTempModal={newGroupDataInTempModal}
      setNewGroupDataInTempModal={setNewGroupDataInTempModal}
      onCreateGroup={handleCreateGroupInModal}
      loading={loading}
      t={t}
    />
  );
};
